import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePresetStore } from '~/stores/usePresetStore';
import type { ShareablePresetData } from '~/types/user-preset';

export default function Share() {
  const { encodedPreset } = useParams<{ encodedPreset: string }>();
  const decodePresetFromUrl = usePresetStore(state => state.decodePresetFromUrl);
  const importFromUrl = usePresetStore(state => state.importFromUrl);
  const [imported, setImported] = useState(false);

  const presetData: ShareablePresetData | null = useMemo(
    () => (encodedPreset ? decodePresetFromUrl(encodedPreset) : null),
    [encodedPreset, decodePresetFromUrl]
  );

  if (!presetData) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold mb-2">Invalid Preset Link</h2>
        <p className="mb-4">This preset link is invalid or corrupted. Please check the URL and try again.</p>
        <Link to="/" className="underline">Back to app</Link>
      </div>
    );
  }

  const handleImport = () => {
    if (!encodedPreset) return;
    importFromUrl(encodedPreset);
    setImported(true);
  };

  const ccCount = presetData.ccSnapshot.length;

  return (
    <div className="p-6 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-4">{presetData.name}</h2>

      <div className="space-y-2 mb-6 text-sm">
        <p><span className="opacity-60">Device:</span> {presetData.deviceType}</p>
        <p><span className="opacity-60">Root Note:</span> {presetData.pattern.rootNote.note}</p>
        <p><span className="opacity-60">Scale:</span> {presetData.pattern.scale.label} ({presetData.pattern.scaleMode})</p>
        <p><span className="opacity-60">Pattern:</span> {presetData.pattern.pattern.name}</p>
        <p><span className="opacity-60">Tempo:</span> {presetData.pattern.tempo} BPM</p>
        {ccCount > 0 && (
          <p><span className="opacity-60">Sound:</span> {ccCount} CC values</p>
        )}
      </div>

      {imported ? (
        <div className="space-y-2">
          <p className="text-green-400 font-medium">Preset saved to your library!</p>
          <Link to="/" className="underline text-sm">Back to app</Link>
        </div>
      ) : (
        <button
          onClick={handleImport}
          className="px-4 py-2 bg-white text-black rounded font-medium hover:bg-gray-200 transition-colors"
        >
          Save Preset
        </button>
      )}
    </div>
  );
}
